import { Toaster as SonnerToaster } from 'sonner';
import { useTheme } from '@vitness/theme';

/**
 * Toast notifications backed by `sonner`. We chose sonner over Radix Toast for
 * its imperative `toast()` API (call from anywhere, no per-toast JSX wiring),
 * built-in stacking/swipe/auto-dismiss, and an accessible `aria-live` region.
 * `sonner` is an internal implementation detail — consumers import only
 * `Toaster` and `toast` from `@vitness/ui`, never `sonner` directly.
 *
 * Mount `<Toaster />` once near the app root. Trigger toasts with the `toast`
 * helper: `toast.success('Saved')`, `toast.error(...)`, `toast(...)`, etc.
 */
export type ToasterProps = Omit<
  React.ComponentProps<typeof SonnerToaster>,
  'theme' | 'toastOptions'
>;

/**
 * Token-driven toast viewport. Inherits the active Vitness theme (dark/light)
 * and styles toasts with `--vn-*` surface, border, and text variables so they
 * match the rest of the design system across accents.
 */
export function Toaster({ position = 'bottom-right', ...props }: ToasterProps) {
  const { mode } = useTheme();

  return (
    <SonnerToaster
      position={position}
      theme={mode}
      toastOptions={{
        style: {
          background: 'var(--vn-surface-card)',
          border: '1px solid var(--vn-border-default)',
          color: 'var(--vn-text-primary)',
          fontFamily: 'var(--vn-font-body)',
          borderRadius: 'var(--vn-radius-lg, 8px)',
        },
      }}
      {...props}
    />
  );
}

/**
 * Imperative toast trigger. Re-exported from sonner so the dependency stays
 * encapsulated within `@vitness/ui` — consumers never import sonner directly.
 */
export { toast } from 'sonner';
